import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2, Image as ImageIcon, Trash2, ChevronDown, ChevronUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const FIELDS = [
  { key: 'practice_name',        label: 'Practice / business name',  group: 'identity' },
  { key: 'address_line1',        label: 'Address line 1',             group: 'address' },
  { key: 'address_line2',        label: 'Address line 2',             group: 'address' },
  { key: 'city',                 label: 'City',                       group: 'address' },
  { key: 'postal_code',          label: 'Postal code',                group: 'address' },
  { key: 'country',              label: 'Country',                    group: 'address' },
  { key: 'phone',                label: 'Phone',                      group: 'contact' },
  { key: 'email',                label: 'Email',                      group: 'contact' },
  { key: 'website',              label: 'Website',                    group: 'contact' },
  { key: 'registration_number',  label: 'Registration / HPCSA number', group: 'reg' },
  { key: 'vat_number',           label: 'VAT / tax number',           group: 'reg' },
  { key: 'bank_name',            label: 'Bank name',                  group: 'banking' },
  { key: 'bank_account_name',    label: 'Account name',               group: 'banking' },
  { key: 'bank_account_number',  label: 'Account number',             group: 'banking' },
  { key: 'bank_branch_code',     label: 'Branch code',                group: 'banking' },
  { key: 'bank_swift',           label: 'SWIFT (international)',      group: 'banking' },
];

const EMPTY = FIELDS.reduce((a, f) => ({ ...a, [f.key]: '' }), { logo_data_url: '' });

export default function PracticeHeaderCard() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(EMPTY);
  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/invoices/practice-header`, { headers: auth() });
        setData({ ...EMPTY, ...(r.data?.header || {}) });
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (key, value) => setData((prev) => ({ ...prev, [key]: value }));

  const handleLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file (PNG/JPG/SVG)');
      return;
    }
    if (file.size > 250 * 1024) {
      toast.error('Logo too large — max 250KB. Try resizing.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => set('logo_data_url', ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/invoices/practice-header`, data, { headers: auth() });
      toast.success('Practice header saved. It will appear on every new invoice.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  const fieldsByGroup = (g) => FIELDS.filter((f) => f.group === g);
  const hasLogo = !!data.logo_data_url;

  return (
    <Card
      className="bg-white rounded-3xl border border-stone-100 mb-8 overflow-hidden"
      data-testid="practice-header-card"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full p-6 flex items-center justify-between text-left"
        data-testid="practice-header-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-primary-900">
              Practice Header (for invoices &amp; documents)
            </h3>
            <p className="text-sm text-stone-500">
              Set up once — automatically added to every invoice you generate.
            </p>
          </div>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-stone-400" /> : <ChevronDown className="w-5 h-5 text-stone-400" />}
      </button>

      {open && (
        <div className="px-6 pb-6 space-y-6">
          {/* Logo */}
          <div>
            <Label className="text-sm font-semibold mb-2 block">Practice logo (optional)</Label>
            <div className="flex items-center gap-4">
              <div
                className="w-24 h-24 rounded-2xl border-2 border-dashed border-stone-200 flex items-center justify-center bg-stone-50 overflow-hidden"
                data-testid="practice-logo-preview"
              >
                {hasLogo ? (
                  <img
                    src={data.logo_data_url}
                    alt="Practice logo"
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <ImageIcon className="w-8 h-8 text-stone-300" />
                )}
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="logo-upload" className="cursor-pointer">
                  <span className="inline-flex items-center px-3 py-1.5 rounded-lg border border-stone-200 hover:bg-stone-50 text-sm">
                    {hasLogo ? 'Replace logo' : 'Upload logo'}
                  </span>
                  <input
                    id="logo-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleLogo}
                    data-testid="practice-logo-input"
                  />
                </Label>
                {hasLogo && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => set('logo_data_url', '')}
                    className="text-red-600"
                    data-testid="practice-logo-remove"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
                  </Button>
                )}
                <p className="text-xs text-stone-500">PNG / JPG / SVG, max 250KB.</p>
              </div>
            </div>
          </div>

          {/* Identity */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {fieldsByGroup('identity').map((f) => (
              <div key={f.key} className="space-y-1 sm:col-span-2">
                <Label className="text-sm">{f.label}</Label>
                <Input
                  value={data[f.key] || ''}
                  onChange={(e) => set(f.key, e.target.value)}
                  data-testid={`practice-${f.key}`}
                />
              </div>
            ))}
          </div>

          {/* Address */}
          <div>
            <h4 className="text-sm font-semibold text-stone-700 mb-2">Address</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {fieldsByGroup('address').map((f) => (
                <div key={f.key} className="space-y-1">
                  <Label className="text-sm text-stone-500">{f.label}</Label>
                  <Input
                    value={data[f.key] || ''}
                    onChange={(e) => set(f.key, e.target.value)}
                    data-testid={`practice-${f.key}`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm font-semibold text-stone-700 mb-2">Contact</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {fieldsByGroup('contact').map((f) => (
                <div key={f.key} className="space-y-1">
                  <Label className="text-sm text-stone-500">{f.label}</Label>
                  <Input
                    value={data[f.key] || ''}
                    onChange={(e) => set(f.key, e.target.value)}
                    data-testid={`practice-${f.key}`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Reg / Tax */}
          <div>
            <h4 className="text-sm font-semibold text-stone-700 mb-2">Registration &amp; tax</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {fieldsByGroup('reg').map((f) => (
                <div key={f.key} className="space-y-1">
                  <Label className="text-sm text-stone-500">{f.label}</Label>
                  <Input
                    value={data[f.key] || ''}
                    onChange={(e) => set(f.key, e.target.value)}
                    data-testid={`practice-${f.key}`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Banking */}
          <div>
            <h4 className="text-sm font-semibold text-stone-700 mb-2">Banking details (printed on invoices)</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {fieldsByGroup('banking').map((f) => (
                <div key={f.key} className="space-y-1">
                  <Label className="text-sm text-stone-500">{f.label}</Label>
                  <Input
                    value={data[f.key] || ''}
                    onChange={(e) => set(f.key, e.target.value)}
                    data-testid={`practice-${f.key}`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="practice-header-save"
            >
              {saving ? 'Saving…' : 'Save practice header'}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
